/**
 * TikTokTargetingPanel
 * Platform-specific targeting UI for TikTok Ads
 * Includes interest categories, behaviors, and smart targeting
 */

import React, { useState, useEffect } from 'react';
import { Users, Zap, Heart, Target, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import InterestSearch from './InterestSearch';
import LocationSearch from './LocationSearch';
import AudienceEstimate from './AudienceEstimate';
import AITargetingHelper from './AITargetingHelper';

export default function TikTokTargetingPanel({
    brandId,
    userId,
    brand, // Full brand object for AI suggestions
    targeting = {},
    onChange,
    isConnected,
    accountId = null,
    funnelStage = 'TOFU'
}) {
    const [customAudiences, setCustomAudiences] = useState([]);
    const [loadingAudiences, setLoadingAudiences] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        demographics: true,
        locations: true,
        interests: true,
        behaviors: false,
        audiences: false,
        smart: false
    });

    useEffect(() => {
        if (isConnected && accountId) {
            loadAudiences();
        }
    }, [isConnected, accountId]);

    const loadAudiences = async () => {
        setLoadingAudiences(true);
        try {
            const response = await fetch(`/api/targeting/tiktok/audiences?brandId=${brandId}&userId=${userId}`);
            if (response.ok) {
                const data = await response.json();
                setCustomAudiences(data.audiences || []);
            }
        } catch (err) {
            console.error('Error loading TikTok audiences:', err);
        } finally {
            setLoadingAudiences(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const updateTargeting = (key, value) => {
        onChange({ ...targeting, [key]: value });
    };

    const updateDemographics = (field, value) => {
        onChange({
            ...targeting,
            demographics: { ...targeting.demographics, [field]: value }
        });
    };

    const handleInterestSelect = (itemOrItems) => {
        const current = targeting.detailedTargeting?.tiktok?.interestCategories || [];
        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            tiktok: { ...targeting.detailedTargeting?.tiktok, interestCategories: [...current, ...items] }
        });
    };

    const handleInterestRemove = (item) => {
        const current = targeting.detailedTargeting?.tiktok?.interestCategories || [];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            tiktok: { ...targeting.detailedTargeting?.tiktok, interestCategories: current.filter(i => i.id !== item.id) }
        });
    };

    const handleBehaviorSelect = (itemOrItems) => {
        const current = targeting.detailedTargeting?.tiktok?.behaviors || [];
        const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            tiktok: { ...targeting.detailedTargeting?.tiktok, behaviors: [...current, ...items] }
        });
    };

    const handleBehaviorRemove = (item) => {
        const current = targeting.detailedTargeting?.tiktok?.behaviors || [];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            tiktok: { ...targeting.detailedTargeting?.tiktok, behaviors: current.filter(b => b.id !== item.id) }
        });
    };

    const handleLocationSelect = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', { ...targeting.locations, included: [...current, loc] });
    };

    const handleLocationRemove = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', { ...targeting.locations, included: current.filter(l => l.id !== loc.id) });
    };

    return (
        <div className="tiktok-targeting-panel">
            {/* Smart Targeting Toggle */}
            <div className="targeting-section smart-section">
                <div className="smart-header">
                    <Zap size={16} />
                    <div className="smart-info">
                        <span className="smart-title">Smart Targeting</span>
                        <span className="smart-desc">Let TikTok automatically optimize targeting</span>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={targeting.smartTargetingEnabled || false}
                            onChange={(e) => updateTargeting('smartTargetingEnabled', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                    </label>
                </div>
            </div>

            {/* Demographics */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('demographics')}>
                    <Users size={16} />
                    <span className="section-title">Demographics</span>
                    {expandedSections.demographics ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.demographics && (
                    <div className="section-content">
                        <div className="form-group">
                            <label>Age Range (13-55+)</label>
                            <div className="age-range">
                                <select value={targeting.demographics?.ageMin || 13} onChange={(e) => updateDemographics('ageMin', parseInt(e.target.value))}>
                                    {[13, 18, 25, 35, 45, 55].map(age => (<option key={age} value={age}>{age}</option>))}
                                </select>
                                <span>to</span>
                                <select value={targeting.demographics?.ageMax || 55} onChange={(e) => updateDemographics('ageMax', parseInt(e.target.value))}>
                                    {[17, 24, 34, 44, 54, 55].map(age => (<option key={age} value={age}>{age}{age === 55 ? '+' : ''}</option>))}
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Gender</label>
                            <div className="gender-options">
                                {['all', 'male', 'female'].map(g => (
                                    <button
                                        key={g}
                                        type="button"
                                        className={`gender-btn ${(targeting.demographics?.genders || ['all']).includes(g) ? 'active' : ''}`}
                                        onClick={() => updateDemographics('genders', g === 'all' ? ['all'] : [g])}
                                    >
                                        {g === 'all' ? 'All' : g.charAt(0).toUpperCase() + g.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Locations */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('locations')}>
                    <Target size={16} />
                    <span className="section-title">Locations</span>
                    {(targeting.locations?.included?.length || 0) > 0 && (
                        <span className="section-count">{targeting.locations.included.length}</span>
                    )}
                    {expandedSections.locations ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.locations && (
                    <div className="section-content">
                        <LocationSearch
                            platform="tiktok"
                            brandId={brandId}
                            userId={userId}
                            selectedLocations={targeting.locations?.included || []}
                            onSelect={handleLocationSelect}
                            onRemove={handleLocationRemove}
                            showExclusions={false}
                            placeholder="Search locations..."
                        />
                    </div>
                )}
            </div>

            {/* Interest Categories */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('interests')}>
                    <Heart size={16} />
                    <span className="section-title">Interest Categories</span>
                    {(targeting.detailedTargeting?.tiktok?.interestCategories?.length || 0) > 0 && (
                        <span className="section-count">{targeting.detailedTargeting.tiktok.interestCategories.length}</span>
                    )}
                    {expandedSections.interests ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.interests && (
                    <div className="section-content">
                        <AITargetingHelper
                            platform="tiktok"
                            type="interest"
                            brandId={brandId}
                            userId={userId}
                            brand={brand}
                            selectedItems={targeting.detailedTargeting?.tiktok?.interestCategories || []}
                            onSelect={handleInterestSelect}
                            funnelStage={funnelStage}
                            disabled={!isConnected}
                        />
                        <InterestSearch
                            platform="tiktok"
                            type="interest"
                            brandId={brandId}
                            userId={userId}
                            selectedItems={targeting.detailedTargeting?.tiktok?.interestCategories || []}
                            onSelect={handleInterestSelect}
                            onRemove={handleInterestRemove}
                            placeholder="Search interest categories..."
                        />
                    </div>
                )}
            </div>

            {/* Behaviors */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('behaviors')}>
                    <Zap size={16} />
                    <span className="section-title">Behaviors</span>
                    {(targeting.detailedTargeting?.tiktok?.behaviors?.length || 0) > 0 && (
                        <span className="section-count">{targeting.detailedTargeting.tiktok.behaviors.length}</span>
                    )}
                    {expandedSections.behaviors ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.behaviors && (
                    <div className="section-content">
                        <AITargetingHelper
                            platform="tiktok"
                            type="behavior"
                            brandId={brandId}
                            userId={userId}
                            brand={brand}
                            selectedItems={targeting.detailedTargeting?.tiktok?.behaviors || []}
                            onSelect={handleBehaviorSelect}
                            funnelStage={funnelStage}
                            disabled={!isConnected}
                        />
                        <InterestSearch
                            platform="tiktok"
                            type="behavior"
                            brandId={brandId}
                            userId={userId}
                            selectedItems={targeting.detailedTargeting?.tiktok?.behaviors || []}
                            onSelect={handleBehaviorSelect}
                            onRemove={handleBehaviorRemove}
                            placeholder="Search behaviors (video interactions, hashtags)..."
                        />
                    </div>
                )}
            </div>

            {/* Audience Estimate */}
            <div className="estimate-section">
                <AudienceEstimate
                    platform="tiktok"
                    brandId={brandId}
                    userId={userId}
                    targeting={targeting}
                />
            </div>

            <style>{`
                .tiktok-targeting-panel { display: flex; flex-direction: column; gap: 0.75rem; }
                .targeting-section { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; }
                .smart-section { border-color: var(--color-primary); }
                .smart-header { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem 1rem; }
                .smart-info { flex: 1; }
                .smart-title { display: block; font-size: 0.875rem; font-weight: 500; color: var(--color-text-primary); }
                .smart-desc { font-size: 0.75rem; color: var(--color-text-tertiary); }
                .toggle-switch { position: relative; width: 44px; height: 24px; }
                .toggle-switch input { opacity: 0; width: 0; height: 0; }
                .toggle-slider { position: absolute; cursor: pointer; inset: 0; background: var(--color-border); border-radius: 24px; transition: 0.3s; }
                .toggle-slider:before { content: ''; position: absolute; height: 18px; width: 18px; left: 3px; bottom: 3px; background: white; border-radius: 50%; transition: 0.3s; }
                .toggle-switch input:checked + .toggle-slider { background: var(--color-primary); }
                .toggle-switch input:checked + .toggle-slider:before { transform: translateX(20px); }
                .section-header { display: flex; align-items: center; gap: 0.5rem; width: 100%; padding: 0.75rem 1rem; background: var(--color-bg-primary); border: none; text-align: left; cursor: pointer; color: var(--color-text-primary); }
                .section-title { flex: 1; font-size: 0.875rem; font-weight: 500; color: var(--color-text-primary); }
                .section-count { padding: 0.125rem 0.5rem; background: var(--color-primary); color: var(--color-text-inverted); border-radius: 10px; font-size: 0.75rem; }
                .section-content { padding: 1rem; border-top: 1px solid var(--color-border); }
                .form-group { margin-bottom: 1rem; }
                .form-group:last-child { margin-bottom: 0; }
                .form-group label { display: block; font-size: 0.8125rem; font-weight: 500; color: var(--color-text-tertiary); margin-bottom: 0.5rem; }
                .age-range { display: flex; align-items: center; gap: 0.5rem; }
                .age-range select { padding: 0.5rem; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 4px; font-size: 0.875rem; color: var(--color-text-primary); }
                .gender-options { display: flex; gap: 0.5rem; }
                .gender-btn { padding: 0.5rem 1rem; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 4px; font-size: 0.875rem; color: var(--color-text-tertiary); cursor: pointer; }
                .gender-btn.active { background: var(--color-primary); border-color: var(--color-primary); color: var(--color-text-inverted); }
                .estimate-section { margin-top: 0.5rem; }
            `}</style>
        </div>
    );
}
